/**
 * <s-delivery-estimate> — estimated delivery timeline.
 *
 * Counts business days (data-days, 0=Sun..6=Sat) in the shop time zone (data-timezone).
 * Orders before the cutoff hour (data-cutoff) start today; later orders start the next
 * business day. Writes formatted dates into [data-date="<key>"] elements:
 * ship-start/ship-end (data-ship-min/max) and delivery-start/delivery-end (data-delivery-min/max).
 * Shows [data-state="today"] with a live countdown before the cutoff, otherwise [data-state="later"].
 */
class SDeliveryEstimate extends HTMLElement {
  #timer;

  connectedCallback() {
    this.cutoff = Number(this.dataset.cutoff ?? 14);
    this.days = (this.dataset.days || '1,2,3,4,5').split(',').map(Number);
    const timeZone = this.dataset.timezone || 'Europe/Madrid';
    this.clock = new Intl.DateTimeFormat('en-GB', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    });
    this.dateFormat = new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    this.output = this.querySelector('[data-countdown-time]');
    this.lastKey = '';
    this.#tick();
    this.#timer = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  #addBusinessDays(date, count) {
    const d = new Date(date);
    let added = 0;
    while (added < count) {
      d.setUTCDate(d.getUTCDate() + 1);
      if (this.days.includes(d.getUTCDay())) added += 1;
    }
    return d;
  }

  #tick() {
    const p = Object.fromEntries(this.clock.formatToParts(new Date()).map((x) => [x.type, x.value]));
    const today = new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day)));
    const elapsed = Number(p.hour) * 3600 + Number(p.minute) * 60 + Number(p.second);
    const remaining = this.cutoff * 3600 - elapsed;
    const beforeCutoff = this.days.includes(today.getUTCDay()) && remaining > 0;

    // Orders after the cutoff (or on non-working days) start from the next business day.
    const start = beforeCutoff ? today : this.#addBusinessDays(today, 1);
    const key = `${start.toISOString()}-${beforeCutoff}`;
    if (key !== this.lastKey) {
      this.lastKey = key;
      const n = (name, fallback) => Number(this.dataset[name] ?? fallback);
      const dates = {
        'ship-start': this.#addBusinessDays(start, n('shipMin', 1)),
        'ship-end': this.#addBusinessDays(start, n('shipMax', 2)),
        'delivery-start': this.#addBusinessDays(start, n('deliveryMin', 6)),
        'delivery-end': this.#addBusinessDays(start, n('deliveryMax', 14)),
      };
      for (const el of this.querySelectorAll('[data-date]')) {
        const date = dates[el.dataset.date];
        if (date) el.textContent = this.dateFormat.format(date);
      }
      for (const el of this.querySelectorAll('[data-state]')) el.hidden = el.dataset.state !== (beforeCutoff ? 'today' : 'later');
    }

    if (beforeCutoff && this.output) {
      const pad = (v) => String(v).padStart(2, '0');
      const h = Math.floor(remaining / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = remaining % 60;
      this.output.textContent = `${h}h ${pad(m)}m ${pad(s)}s`;
    }
  }
}

if (!customElements.get('s-delivery-estimate')) customElements.define('s-delivery-estimate', SDeliveryEstimate);
